<script setup lang="ts">
  import { Pagination } from '~/enum'

  definePageMeta({
    keepalive: true,
  })

  const { t } = useResolvedI18n()
  const localePath = useLocalePath()
  const route = useRoute()

  useHead({
    title: () => t('news.listTitle'),
    meta: [{ name: 'referrer', content: 'no-referrer' }],
  })

  const query = computed(() => String(route.query.q || '').trim())
  const {
    list: newsList,
    total,
    hasMore,
    loadingMore,
    loadMore,
    pending,
    onListLeave,
    onListEnter,
    markPreserveForDetail,
  } = await usePublicNewsList({ q: query, paginated: true, pageSize: Pagination.PAGE_SIZE })

  function isPlaceholderCover(cover: string) {
    return !cover || cover.includes('image_error.png')
  }

  onBeforeRouteLeave((to) => {
    onListLeave?.(to.path)
  })

  // keepalive: skip first activate (setup already loaded); later re-entry decides preserve vs reset
  const activatedOnce = ref(false)
  onActivated(async () => {
    if (!activatedOnce.value) {
      activatedOnce.value = true
      return
    }
    await onListEnter?.()
  })
</script>

<template>
  <section class="page-banner" style="background-image: url('/images/campus-bg.jpg')">
    <h2>{{ t('news.banner') }}</h2>
    <div class="breadcrumb">
      <NuxtLink :to="localePath('/')">{{ t('common.home') }}</NuxtLink>
      &gt; {{ t('news.breadcrumb') }}
    </div>
  </section>

  <section class="content-section">
    <div class="container">
      <p v-if="query" class="news-search-hint">
        {{ t('news.searchResult', { q: query, n: total }) }}
      </p>

      <div v-if="newsList.length" class="news-page-list">
        <NuxtLink
          v-for="item in newsList"
          :key="item.slug"
          :to="localePath(`/news/${item.slug}`)"
          class="news-page-item"
          @click="markPreserveForDetail"
        >
          <img
            class="news-page-thumb"
            :class="{ 'is-placeholder': isPlaceholderCover(item.cover) }"
            :src="item.cover"
            :alt="item.title"
            referrerpolicy="no-referrer"
            loading="lazy"
          />
          <div class="news-page-body">
            <h3>{{ item.title }}</h3>
            <time>{{ item.date }}</time>
          </div>
        </NuxtLink>
      </div>

      <p v-else-if="!pending" class="news-empty">{{ t('news.empty') }}</p>

      <div v-if="newsList.length" class="news-page-footer">
        <n-button v-if="hasMore" quaternary type="info" :disabled="loadingMore" @click="loadMore">
          {{ loadingMore ? t('news.loadingMore') : t('news.loadMore') }}
        </n-button>
        <p v-else class="news-no-more">{{ t('news.noMore') }}</p>
      </div>
    </div>
  </section>
</template>
